<template>
  <div
    :class="['phoenix-keyboard-wrapper', 'tablet', `keyboard-${keyboardType}`]"
    id="phoenix-keyboard-wrapper"
  >
    <div
      :class="[
        'MobileOnscreenKeyboard',
        { outline: showOutline, disabled: keyboardLocked, showSpecialKeys }
      ]"
    >
      <div v-for="row in displayKeys" :key="row.name" :class="[`row ${row.name}`, theme]">
        <component
          :is="getKwtKey(key)"
          v-for="key in row.keys"
          :key="key.name"
          :name="key.name"
          :character="key.character"
          :alt="key.alt"
          :shift="key.shift"
          :special="key.special"
          :hold-hand="key.hold"
          :row="row.name"
          :press="handleKeyDown"
          :release="handleKeyUp"
        ></component>
      </div>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import { characterMap, keyLayout } from 'constants/touch-keyboard';
import { convertSymbolToName } from 'utils/key-util';
import BaseKeyboard from 'mixins/BaseKeyboard';
import TouchKey from './keys/TouchKey.vue';
import TouchHoldKey from './keys/TouchHoldKey.vue';

const keyboard = createNamespacedHelpers('keyboard');
const keyboardName = 'MobileOnscreenKeyboard';
const keys = {
  standardTouch: TouchKey,
  touchHold: TouchHoldKey
};

export default {
  name: keyboardName,

  components: {
    ...keys
  },

  mixins: [BaseKeyboard],

  props: {
    layoutType: {
      default: 'standard',
      validator: layout => ['standard', 'touch'].includes(layout)
    },
    theme: {
      default: 'light',
      validator: theme => ['light', 'dark'].includes(theme)
    }
  },

  computed: {
    ...keyboard.mapState(['capsLock', 'holdKeyPressed', 'showSpecialKeys']),
    displayKeys() {
      if (!this.hideNumberRow) return this.keyLayout;
      return this.keyLayout.filter(row => row.name !== 'rowNumber');
    }
  },

  data() {
    return {
      keyboardName,
      characterMap,
      keyLayout,
      processKeyPress: null,
      kwtKey: '',
      shiftKeyDown: false,
      cmdKeyDown: false
    };
  },

  created() {
    if (this.isTouchHold) {
      this.processKeyPress = this.processTouchHoldKey;
      return;
    }

    this.processKeyPress = this.processStandardKey;
  },

  methods: {
    ...keyboard.mapMutations(['setCapsLock', 'toggleSpecialKeys']),

    getKwtKey(key) {
      if (this.isOneHandedTypingHandActivity) {
        return 'standardTouch';
      }

      if (this.isTouchHold && key.hold && this.targetKey === key.name) {
        return 'touchHold';
      }

      return 'standardTouch';
    },

    doNotHandleKey(key) {
      return (
        key !== 'Backspace' &&
        (this.keyboardDisabled ||
          key.toLowerCase() === 'caplock' ||
          this.keyNotUsedInActivity(key) ||
          this.keyIsPressed({ key }))
      );
    },

    keyIsDisabled(key) {
      const keyName = convertSymbolToName(key.toLowerCase());
      const keyElement = document.getElementsByClassName(`key${keyName} ghosting`)[0];

      return !keyElement.classList.contains('active');
      // return false;
    },

    modifyWithShift(key) {
      const specialKeys = {
        ';': ':',
        ',': '!',
        '.': '?'
      };

      const isLetter = key.length === 1 && /[a-z]/.test(key);
      return isLetter ? key.toUpperCase() : specialKeys[key];
    },

    isShiftKeyTarget(key) {
      return this.targetKey.includes('shift') && this.isShiftKey(key);
    },

    handleKeyDown(keyCharacter) {
      let key = keyCharacter;
      if (this.isTouchHold && this.holdKeyIsPressed(key)) {
        this.pressHoldKey();
        this.enableKeyboard();
        if (!this.isShiftKeyTarget(key)) return;
      }

      if (this.keyIsDisabled(key)) return;

      if (key === '123' || key === 'abc') {
        this.toggleSpecialKeys();
        return;
      }

      if (this.isShiftKey(key)) {
        this.shiftKeyDown = true;
      }

      if (this.isCmdKey(key)) {
        this.cmdKeyDown = true;
      }

      if (
        (this.shiftKeyDown && !this.isShiftKey(key)) ||
        (['shift1', 'shift2'].includes(this.targetKey) && this.holdKeyPressed)
      ) {
        key = this.modifyWithShift(key);
      }

      if (this.checkIpadCapsLock(key)) {
        this.setCapsLock(!this.capsLock);
      }

      if (this.capsLock) {
        // on IPad we manually need to uppercase key when Caps key is ON
        key = key.toUpperCase();
      }

      if (this.doNotHandleKey(key)) return;
      this.processKeyPress(this.normalizeKey(key), {
        withCmd: this.cmdKeyDown,
        withShift: this.shiftKeyDown
      });

      // don't add shift to activeKey array. shift is a 'free' key
      if (!this.isShiftKey(key)) {
        this.addToActiveKeys({ key });
      }
    },

    processTouchHoldKey(key, { withShift }) {
      if (this.keyboardLocked && (this.isOneHandedTypingHandActivity || !this.capsLock)) {
        if (key === this.targetKey) {
          this.pressHoldKey();
        }
      } else {
        this.attemptKey({ key, withShift });
      }
    },

    processStandardKey(key, { withShift }) {
      this.attemptKey({ key, withShift });
    },

    handleKeyUp(keyCharacter) {
      let key = keyCharacter;

      if (this.isTouchHold && key === this.targetKey) {
        this.releaseHoldKey();
        if (key && !this.isShiftKeyTarget(key)) {
          this.disableKeyboard();
          return;
        }
      }

      if (this.keyboardLocked) return;
      if (this.isShiftKey(key)) {
        this.shiftKeyDown = false;
      }
      if (this.isCmdKey(key)) {
        this.cmdKeyDown = false;
      }

      if (this.shiftKeyDown && !this.isShiftKey(key)) {
        key = this.modifyWithShift(key);
      }

      this.removeFromActiveKeys({ key });

      if (this.isTouchHold && key === this.targetKey) {
        this.releaseHoldKey();
      } else {
        this.resetAttemptedKey();
      }
    },

    isShiftKey(keyPressed) {
      const key = keyPressed.toLowerCase();
      // iOS registers Shift as 'Dead'
      return key.includes('shift') || key === 'dead';
    },

    isCmdKey(keyPressed) {
      const key = keyPressed.toLowerCase();
      return key.includes('meta');
    },

    normalizeKey(key) {
      const normalized = {
        enter: 'enter',
        spacebar: ' ',
        space: ' ',
        quotationmark: '"',
        tab: 'tab'
      };

      return normalized[key.toLowerCase()] || key;
    }
  }
};
</script>

<style lang="scss">
.phoenix-keyboard-wrapper .MobileOnscreenKeyboard {
  margin: 0;

  .row {
    width: 100%;
    max-height: 100px;
    background-size: contain;

    div {
      min-width: 7vw;
      height: 7vw;
      flex: 1 auto;

      &.keyshift1 {
        width: 11.5vw;
        height: 7vw;
        background-size: cover;
        background-position: center;
      }

      &.keyshift2 {
        width: 10.5vw;
        height: 7vw;
        background-size: cover;
        background-position: center;
      }

      &.keybackspace {
        width: 9%;
      }

      &.keyenter {
        max-width: 7vw;
      }

      &.keytab {
        width: 5.7vw;
        min-width: 4vw;
      }

      &.keyspace {
        width: 35vw;
        background-size: cover;
        background-position: center;
      }

      &.keycaplock {
        max-width: 7vw;
      }

      &.keyspecialnumber,
      &.keyspecialletter,
      &.key123,
      &.keyabc {
        min-width: 11.5vw;
        background-size: cover;
        background-position: center;
      }
    }

    section {
      display: flex;
      width: 77%;
      margin: 0 auto;
    }

    section .keyspecial {
      height: 7vw;
      max-width: 17vw;
    }
  }

  .row4 {
    background-color: #fff;

    div {
      max-height: 6vw;
    }
  }
}

.keyboard-KeyboardK2.tablet {
  &.tablet .ghosting {
    background-size: cover;
    min-width: 7vw;
    max-height: 7vw;

    &.keyspace {
      max-width: 32vw;
      height: 4.3vw;
    }

    &.keyshift2 {
      max-width: 9.5vw !important;
    }

    &.keyshift1 {
      max-width: 7vw;
      background-size: contain;
    }

    &.key123,
    &.keyabc {
      height: 4.5vw;
    }

    &.keytab {
      min-width: 4vw;
      width: 4.7vw;
    }
  }
}
</style>
